import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export const HeaderContainer = styled.header`
  background-color: #18382F;
  border-bottom: 1px solid rgba(232, 201, 139, 0.25);
  padding: 0 1.5rem;
  height: 64px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-template-columns: none;
  }
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: start;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const MobileDonateSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 0.75rem 1rem;
    background-color: var(--background-primary);
    border-bottom: 1px solid var(--border-color);
    box-shadow: var(--shadow-sm);
  }
`;

export const DonateLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #18382F;
  color: var(--text-inverse);
  text-decoration: none;
  border: 1px solid #E8C98B; /* same green as the header, so it needs an edge to read as a button */
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);

  &:hover {
    background: #24493E;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(24, 56, 47, 0.35);
    text-decoration: none;
    color: var(--text-inverse);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    display: none; /* Hide on mobile in LeftSection */
  }
`;

export const MobileDonateLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem;
  background: #18382F;
  color: var(--text-inverse);
  text-decoration: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(24, 56, 47, 0.25);
  width: auto;
  white-space: nowrap;

  &:hover {
    background: #24493E;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(24, 56, 47, 0.35);
    text-decoration: none;
    color: var(--text-inverse);
  }

  &:active {
    transform: translateY(0);
  }

  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.5rem;
    font-size: 0.8rem;
    gap: 0.375rem;
  }
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    transform: scale(1.02);
  }

  img {
    height: 40px;
    width: auto;

    @media (max-width: 768px) {
      height: 32px;
    }
  }

  h1 {
    color: #E8C98B;
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.5rem;
    font-weight: 500;
    letter-spacing: -0.025em;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  span {
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
  }
`;


export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: end;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const NotificationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #E8C98B;
  }
`;

export const NotificationBadge = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background-color: var(--secondary-color);
  color: var(--text-inverse);
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0;
  min-width: 18px;
  text-align: center;
`;

export const UserMenu = styled.div`
  position: relative;
`;

export const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

export const FallbackAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #E8C98B;
  color: #18382F;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

export const UserInfo = styled.div`
  text-align: left;

  .name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #fff;
  }

  .role {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;

  @media (max-width: 768px) {
    right: -1rem;
    min-width: 180px;
  }
`;

export const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: var(--transition);
  color: var(--text-primary);
  font-size: 0.875rem;

  &:hover {
    background-color: var(--background-secondary);
  }

  &:first-child {
    border-bottom: 1px solid var(--border-color);
  }
`;

export const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #E8C98B;
  }
`;
